// Which screen the browser is allowed to see, folded from what the API answered
// (#231).
//
// A reducer, for the reason the run page's projection is one: the decisions are
// then a pure function of the answers, and every one of them is a test with no
// fetch, no DOM and no clock in it. The component below this holds the effects
// and nothing else.
//
// **The gate comes before the path.** `runIdFromLocation` says which
// authenticated screen a location asks for; the gate says whether any of them is
// yours. So an unauthenticated visit to `/` renders sign-in rather than the
// picker, and an unauthenticated visit to `/runs/<id>` renders sign-in rather
// than a run page whose stream would be refused.

import type { AccountsRead } from "../api/accounts";
import type { SpendView } from "../api/billing";
import type { ChangeRow } from "../api/changes";
import { ApiError } from "../api/http";
import type { ConsentRead } from "../api/consent";
import type { CustomerRow, InviteRow, OrgRow } from "../api/customers";
import type { MemberRow } from "../api/members";
import type { GrantsRead } from "../api/grants";
import { groupKeyOf, type AnsweredPage, type InboxRead } from "../api/inbox";
import type { InvitePreview } from "../api/invites";
import type { WaitingInvitation } from "../api/invites";
import type { Membership } from "../api/memberships";
import type { PolicyRead } from "../api/policy";
import type { Role } from "../api/roles";
import type { RunRow } from "../api/runs";
import { newestFirst } from "../api/runs";
import { hasContext, type ContextChoice, type PublicUser, type SessionResponse } from "../api/session";
import type { DeliveryRow, IntegrationRow } from "../api/integrations";
import type { IntegrationsStep } from "./integrationsStep";
import type { ProviderRefusal, ProviderRow, Setup, SourceRow } from "../api/sources";

export type Gate =
  /** Nothing has been read yet: the first `GET /auth/session` is in flight. */
  | { kind: "loading" }
  /** No session, or one that has ended. `notice` says which, when it is worth saying. */
  | { kind: "signed-out"; notice: string | null }
  /** `POST /auth/login` answered `mfa_required`; the token completes it. */
  | { kind: "mfa"; mfaToken: string; methods: string[] }
  /**
   * Signed in to an account whose deletion is requested (#1136): one screen, in place of
   * every page. `deletesAt` is the server's date, or null where it has none to give yet.
   */
  | { kind: "deletion-pending"; deletesAt: string | null }
  /** Signed in, and has not said whose runs it wants. Not an empty list. */
  | { kind: "no-context"; user: PublicUser }
  /** Signed in, with a context: the console proper. */
  | { kind: "ready"; session: SessionResponse };

/**
 * What a door screen has learned so far (#415). One slot for every door,
 * because a browser is at one door at a time and a door's state must not
 * survive a move to another: the reducer resets it on `door-reset`.
 */
export type DoorState =
  /** The token is being spent, or the preview read: the screen says so. */
  | { kind: "busy" }
  /** The invitation the link opens, for the landing page to show before anyone acts. */
  | { kind: "invitation"; invite: InvitePreview }
  /** The door did its work; `message` is what to tell the person, `next` where to go. */
  | { kind: "done"; message: string; next: "runs" | "sign-in" | null }
  /** The token or the request was refused; the screen says why and what to do. */
  | { kind: "failed"; message: string };

export interface ConsoleState {
  gate: Gate;
  /** A door's progress; null when nothing has been asked, which is how a form door starts. */
  door: DoorState | null;
  /**
   * The door at this location was reached under a pending deletion and has not acted
   * (#1171): signed out, the person is shown sign-in rather than the door.
   */
  doorHeld?: boolean;
  /**
   * The contexts this user may select, by name (#414); null while unread. They
   * are the user's rather than the context's, so a context change keeps them:
   * the rail names the current one from this list.
   */
  memberships: Membership[] | null;
  /** A failure reading the list, or selecting the only entry on it. */
  membershipsError: string | null;
  /** The landing page's rows; null while they have not been read. */
  runs: RunRow[] | null;
  /** A failure the run list itself reports, not one that changes the gate. */
  runsError: string | null;
  runsHasMore?: boolean;
  runsLoadingMore?: boolean;
  contextError?: string | null;
  /**
   * The control plane refused the selection for want of a membership (#971), which it says
   * alike for a context that does not exist and one the person does not hold. Any other
   * failure, a refusal for another reason included, is not this.
   */
  contextRefused?: boolean;
  /**
   * The context a link into the console selected, once it is there (#858): what the
   * Inbox's drawer says the person has landed in. Null when no link moved the session,
   * and again once the location names no context.
   */
  linkArrived?: ContextChoice | null;
  /** The settings page's role catalogue; null while it has not been read (#258). */
  roles: Role[] | null;
  /** A failure the roles section reports, on the same terms as `runsError`. */
  rolesError: string | null;
  /** The settings page's remediation policy; null while it has not been read (#260). */
  policy: PolicyRead | null;
  /** A failure the policy section reports, on the same terms as `rolesError`. */
  policyError: string | null;
  /** The consent screen's requests and standing grants; null while unread (#263). */
  consent: ConsentRead | null;
  /** A failure the consent screen reports, on the same terms as `policyError`. */
  consentError: string | null;
  /** The Changes screen's waiting changes; null while unread (#261). */
  changes: ChangeRow[] | null;
  /** A failure the Changes screen reports, on the same terms as `consentError`. */
  changesError: string | null;
  /** The platform admin's customers; null while unread (#417). */
  orgs: OrgRow[] | null;
  orgsError: string | null;
  /** The MSP's customers; null while unread (#417). */
  customers: CustomerRow[] | null;
  customersError: string | null;
  /** The workspace's own usage and bill this period; null while unread (#632, D6). */
  spend: SpendView | null;
  spendError: string | null;
  /** The workspace's members, on Team & roles; null while unread (#806). */
  members: MemberRow[] | null;
  membersError: string | null;
  /** The team's live invitations, on the settings surface; null while unread (#417). */
  invites: InviteRow[] | null;
  invitesError: string | null;
  /**
   * The invitations waiting for **this user**, across every context; null while
   * unread (#440). The user's, not the context's — an invitation exists precisely
   * when they are not a member of the workspace it names — so a context change
   * keeps them, exactly as `memberships` is kept.
   */
  waiting: WaitingInvitation[] | null;
  waitingError: string | null;
  /**
   * What is waiting for **this user** to answer, across every context, grouped by
   * workspace; null while unread (#495). The user's, as `waiting` is, so a context
   * change keeps it: the rail counts it on every screen.
   */
  inbox: InboxRead | null;
  inboxError: string | null;
  /** The workspace's access grants: waiting, standing, past; null while unread (#418). */
  access: GrantsRead | null;
  accessError: string | null;
  /** The workspace's AWS connections and accounts; null while unread (#420). */
  accounts: AccountsRead | null;
  accountsError: string | null;
  /** The workspace's alert sources; null while unread (#422). */
  sources: SourceRow[] | null;
  sourcesError: string | null;
  /** The workspace's model provider, or null for none; undefined while unread (#422). */
  provider: ProviderRow | null | undefined;
  /** When that provider last refused an investigation, or null (#1415). */
  providerRefusal: ProviderRefusal | null;
  providerError: string | null;
  /** The workspace's Jira and Slack connections; null while unread (#492). */
  integrations: IntegrationRow[] | null;
  integrationsError: string | null;
  /**
   * One connection's recent deliveries, by connection id (#579). A key is absent
   * until the person expands that row, `null` while its read is in flight, and a
   * string when the read was refused — so an expanded row can say which of the three
   * it is without a second map.
   *
   * The map itself is `null` while nothing has been read under this session, for the
   * reason every other field here is: it was read under a session, and a sign-out
   * clears it. An empty object would be cleared too, and would not look it.
   */
  deliveries: Record<string, DeliveryRow[] | null | string> | null;
  /** What the run list's empty state is a checklist of; null while unread (#422). */
  setup: Setup | null;
  /** The optional step under the setup checklist (#602); null while unread, and when there is none to show. */
  integrationsStep: IntegrationsStep | null;
}

export const initialConsoleState: ConsoleState = {
  gate: { kind: "loading" },
  door: null,
  memberships: null,
  membershipsError: null,
  runs: null,
  runsError: null,
  roles: null,
  rolesError: null,
  policy: null,
  policyError: null,
  consent: null,
  consentError: null,
  changes: null,
  changesError: null,
  orgs: null,
  orgsError: null,
  customers: null,
  customersError: null,
  spend: null,
  spendError: null,
  members: null,
  membersError: null,
  invites: null,
  invitesError: null,
  waiting: null,
  waitingError: null,
  inbox: null,
  inboxError: null,
  access: null,
  accessError: null,
  accounts: null,
  accountsError: null,
  sources: null,
  sourcesError: null,
  provider: undefined,
  providerRefusal: null,
  providerError: null,
  integrations: null,
  integrationsError: null,
  deliveries: null,
  setup: null,
  integrationsStep: null,
};

export type ConsoleEvent =
  | { type: "session"; session: SessionResponse }
  | { type: "signed-out"; notice: string | null }
  | { type: "deletion-pending"; deletesAt: string | null }
  | { type: "mfa"; mfaToken: string; methods: string[] }
  | { type: "memberships"; memberships: Membership[] }
  | { type: "memberships-failed"; message: string }
  | { type: "door"; door: DoorState | null }
  | { type: "runs"; runs: RunRow[] }
  | { type: "runs-more"; runs: RunRow[] }
  | { type: "runs-loading-more" }
  | { type: "context-changing" }
  | { type: "context-failed"; message: string; refused?: boolean }
  | { type: "link-arrived"; context: ContextChoice | null }
  | { type: "runs-failed"; message: string }
  | { type: "roles"; roles: Role[] }
  | { type: "roles-failed"; message: string }
  | { type: "policy"; policy: PolicyRead }
  | { type: "policy-failed"; message: string }
  | { type: "consent"; consent: ConsentRead }
  | { type: "consent-failed"; message: string }
  | { type: "changes"; changes: ChangeRow[] }
  | { type: "changes-failed"; message: string }
  | { type: "orgs"; orgs: OrgRow[] }
  | { type: "orgs-failed"; message: string }
  | { type: "customers"; customers: CustomerRow[] }
  | { type: "customers-failed"; message: string }
  | { type: "spend"; spend: SpendView }
  | { type: "spend-failed"; message: string }
  | { type: "waiting"; waiting: WaitingInvitation[] }
  | { type: "waiting-failed"; message: string }
  | { type: "inbox"; inbox: InboxRead }
  | { type: "inbox-failed"; message: string }
  /** One group's older answered items (#497), appended under that group. */
  | { type: "inbox-older"; group: string; cursor: string; page: AnsweredPage }
  | { type: "members"; members: MemberRow[] }
  | { type: "members-failed"; message: string }
  | { type: "invites"; invites: InviteRow[] }
  | { type: "invites-failed"; message: string }
  | { type: "access"; access: GrantsRead }
  | { type: "access-failed"; message: string }
  | { type: "accounts"; accounts: AccountsRead }
  | { type: "accounts-failed"; message: string }
  | { type: "sources"; sources: SourceRow[] }
  | { type: "sources-failed"; message: string }
  /** A read carries the provider's last refusal; a save, whose provider is new, has none. */
  | { type: "provider"; provider: ProviderRow | null; lastRefusal?: ProviderRefusal | null }
  | { type: "provider-failed"; message: string }
  | { type: "integrations"; integrations: IntegrationRow[] }
  | { type: "integrations-failed"; message: string }
  | { type: "deliveries-reading"; connectionId: string }
  | { type: "deliveries"; connectionId: string; deliveries: DeliveryRow[] }
  | { type: "deliveries-failed"; connectionId: string; message: string }
  | { type: "delivery-retried"; connectionId: string; delivery: DeliveryRow }
  | { type: "setup"; setup: Setup }
  | { type: "integrations-step"; step: IntegrationsStep | null }
  | { type: "reading" };

/**
 * A refusal that is about the session rather than about the resource (#231).
 *
 * 401 and 409 both mean "not yours to see", and **the status alone cannot say
 * which screen you get**: `records.by_session` answers 409 for a session with
 * no context while the stream answers 401 for that same state (#152 Q4's
 * decision — flagged on #231 as the owner's inconsistency and not this
 * ticket's to fix). Reading either as the answer would send half the no-context
 * cases to sign-in and leave a signed-out user staring at a context form.
 *
 * So neither is read as the answer. Both send the console back to
 * `GET /auth/session`, which is the one route that tells them apart: a 401
 * there is a session that is gone, and a 200 with neither a workspace nor an org
 * is a session that has not chosen. Both routes keep their own status, and the
 * console handles both.
 */
export function refusalNeedsSessionRead(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 401 || error.status === 409);
}

/** The gate a read of `GET /auth/session` puts the console in. */
export function gateForSession(session: SessionResponse): Gate {
  return hasContext(session)
    ? { kind: "ready", session }
    : { kind: "no-context", user: session.user };
}

export function contextOf(gate: Gate): string | null {
  if (gate.kind !== "ready") return null;
  const s = gate.session;
  return JSON.stringify([
    s.user.user_id,
    s.workspace_id,
    s.org_id,
    s.acting_org_id ?? null,
    s.impersonated_by,
  ]);
}

export function consoleReducer(state: ConsoleState, event: ConsoleEvent): ConsoleState {
  switch (event.type) {
    case "context-changing":
      return {
        ...initialConsoleState,
        memberships: state.memberships,
        waiting: state.waiting,
        inbox: state.inbox,
      };
    case "context-failed":
      return { ...state, contextError: event.message, contextRefused: event.refused === true };
    case "link-arrived":
      return { ...state, linkArrived: event.context };
    case "session": {
      const gate = gateForSession(event.session);
      // A context that moved makes the rows on the screen another workspace's:
      // they go with it, rather than sitting under the new rail until the
      // next read lands.
      const kept = contextOf(gate) === contextOf(state.gate);
      return {
        gate,
        door: state.door,
        // The user's, not the context's: they survive a context change.
        memberships: state.memberships,
        membershipsError: state.membershipsError,
        runs: kept ? state.runs : null,
        runsError: kept ? state.runsError : null,
        runsHasMore: kept ? state.runsHasMore : false,
        runsLoadingMore: kept ? state.runsLoadingMore : false,
        // The roles are the context's too: a catalogue read under one workspace
        // must not sit on the settings page under the next (#258).
        roles: kept ? state.roles : null,
        rolesError: kept ? state.rolesError : null,
        // And the remediation policy, for the same reason (#260).
        policy: kept ? state.policy : null,
        policyError: kept ? state.policyError : null,
        // And the consent requests: another workspace's are not this one's to answer (#263).
        consent: kept ? state.consent : null,
        consentError: kept ? state.consentError : null,
        // And the changes: another workspace's cards are not this one's to answer (#261).
        changes: kept ? state.changes : null,
        changesError: kept ? state.changesError : null,
        // The platform admin's list is the user's, not the context's; the MSP's
        // customers and the team's invitations are the context's (#417).
        orgs: state.orgs,
        orgsError: state.orgsError,
        customers: kept ? state.customers : null,
        customersError: kept ? state.customersError : null,
        spend: kept ? state.spend : null,
        spendError: kept ? state.spendError : null,
        members: kept ? state.members : null,
        membersError: kept ? state.membersError : null,
        invites: kept ? state.invites : null,
        invitesError: kept ? state.invitesError : null,
        // The invitations waiting for the user are the user's: accepting one is
        // what *changes* the context, so they cannot be cleared by the change.
        waiting: state.waiting,
        waitingError: state.waitingError,
        inbox: state.inbox,
        inboxError: state.inboxError,
        access: kept ? state.access : null,
        accessError: kept ? state.accessError : null,
        accounts: kept ? state.accounts : null,
        accountsError: kept ? state.accountsError : null,
        sources: kept ? state.sources : null,
        sourcesError: kept ? state.sourcesError : null,
        provider: kept ? state.provider : undefined,
        providerRefusal: kept ? state.providerRefusal : null,
        providerError: kept ? state.providerError : null,
        integrations: kept ? state.integrations : null,
        integrationsError: kept ? state.integrationsError : null,
        deliveries: kept ? state.deliveries : null,
        setup: kept ? state.setup : null,
        integrationsStep: kept ? state.integrationsStep : null,
      };
    }
    case "deletion-pending":
      // Nothing is readable while the account is being deleted, so nothing read is kept.
      return {
        ...consoleReducer(state, { type: "signed-out", notice: null }),
        gate: { kind: "deletion-pending", deletesAt: event.deletesAt },
      };
    case "signed-out":
      // Nothing read under the old session survives it.
      return {
        gate: { kind: "signed-out", notice: event.notice },
        door: null,
        memberships: null,
        membershipsError: null,
        runs: null,
        runsError: null,
        roles: null,
        rolesError: null,
        policy: null,
        policyError: null,
        consent: null,
        consentError: null,
        changes: null,
        changesError: null,
        orgs: null,
        orgsError: null,
        customers: null,
        customersError: null,
        spend: null,
        spendError: null,
        members: null,
        membersError: null,
        invites: null,
        invitesError: null,
        waiting: null,
        waitingError: null,
        inbox: null,
        inboxError: null,
        access: null,
        accessError: null,
        accounts: null,
        accountsError: null,
        sources: null,
        sourcesError: null,
        provider: undefined,
        providerRefusal: null,
        providerError: null,
        integrations: null,
        integrationsError: null,
        deliveries: null,
        setup: null,
        integrationsStep: null,
      };
    case "mfa":
      return {
        gate: { kind: "mfa", mfaToken: event.mfaToken, methods: event.methods },
        door: null,
        memberships: null,
        membershipsError: null,
        runs: null,
        runsError: null,
        roles: null,
        rolesError: null,
        policy: null,
        policyError: null,
        consent: null,
        consentError: null,
        changes: null,
        changesError: null,
        orgs: null,
        orgsError: null,
        customers: null,
        customersError: null,
        spend: null,
        spendError: null,
        members: null,
        membersError: null,
        invites: null,
        invitesError: null,
        waiting: null,
        waitingError: null,
        inbox: null,
        inboxError: null,
        access: null,
        accessError: null,
        accounts: null,
        accountsError: null,
        sources: null,
        sourcesError: null,
        provider: undefined,
        providerRefusal: null,
        providerError: null,
        integrations: null,
        integrationsError: null,
        deliveries: null,
        setup: null,
        integrationsStep: null,
      };
    case "door":
      return { ...state, door: event.door };
    case "memberships":
      return { ...state, memberships: event.memberships, membershipsError: null };
    case "memberships-failed":
      return { ...state, membershipsError: event.message };
    case "runs":
      return {
        ...state,
        runs: event.runs,
        runsError: null,
        runsHasMore: event.runs.length === 50,
        runsLoadingMore: false,
      };
    case "runs-loading-more":
      return { ...state, runsLoadingMore: true, runsError: null };
    case "runs-more": {
      const unique = new Map((state.runs ?? []).map((row) => [row.run_id, row]));
      for (const row of event.runs) if (!unique.has(row.run_id)) unique.set(row.run_id, row);
      return {
        ...state,
        runs: newestFirst([...unique.values()]),
        runsError: null,
        runsLoadingMore: false,
        runsHasMore: event.runs.length === 50 && unique.size > (state.runs?.length ?? 0),
      };
    }
    case "runs-failed":
      return { ...state, runsError: event.message, runsLoadingMore: false };
    case "roles":
      return { ...state, roles: event.roles, rolesError: null };
    case "roles-failed":
      return { ...state, rolesError: event.message };
    case "policy":
      return { ...state, policy: event.policy, policyError: null };
    case "policy-failed":
      return { ...state, policyError: event.message };
    case "consent":
      return { ...state, consent: event.consent, consentError: null };
    case "consent-failed":
      return { ...state, consentError: event.message };
    case "changes":
      return { ...state, changes: event.changes, changesError: null };
    case "changes-failed":
      return { ...state, changesError: event.message };
    case "orgs":
      return { ...state, orgs: event.orgs, orgsError: null };
    case "orgs-failed":
      return { ...state, orgsError: event.message };
    case "customers":
      return { ...state, customers: event.customers, customersError: null };
    case "customers-failed":
      return { ...state, customersError: event.message };
    case "spend":
      return { ...state, spend: event.spend, spendError: null };
    case "spend-failed":
      return { ...state, spendError: event.message };
    case "waiting":
      return { ...state, waiting: event.waiting, waitingError: null };
    case "waiting-failed":
      return { ...state, waitingError: event.message };
    case "inbox":
      return { ...state, inbox: event.inbox, inboxError: null };
    case "inbox-failed":
      return { ...state, inboxError: event.message };
    case "inbox-older":
      if (state.inbox === null) return state;
      return {
        ...state,
        inboxError: null,
        inbox: {
          ...state.inbox,
          groups: state.inbox.groups.map((group) =>
            // Only onto the page it continues: a second answer for the same cursor (a double
            // click, two reads in flight) finds the cursor moved on and appends nothing.
            groupKeyOf(group) === event.group && group.answered_next === event.cursor
              ? {
                  ...group,
                  answered: [...group.answered, ...event.page.answered],
                  answered_next: event.page.answered_next,
                }
              : group,
          ),
        },
      };
    case "members":
      return { ...state, members: event.members, membersError: null };
    case "members-failed":
      return { ...state, membersError: event.message };
    case "invites":
      return { ...state, invites: event.invites, invitesError: null };
    case "invites-failed":
      return { ...state, invitesError: event.message };
    case "access":
      return { ...state, access: event.access, accessError: null };
    case "access-failed":
      return { ...state, accessError: event.message };
    case "accounts":
      return { ...state, accounts: event.accounts, accountsError: null };
    case "accounts-failed":
      return { ...state, accountsError: event.message };
    case "sources":
      return { ...state, sources: event.sources, sourcesError: null };
    case "sources-failed":
      return { ...state, sourcesError: event.message };
    case "provider":
      return { ...state, provider: event.provider, providerRefusal: event.lastRefusal ?? null, providerError: null };
    case "provider-failed":
      return { ...state, providerError: event.message };
    case "integrations":
      return { ...state, integrations: event.integrations, integrationsError: null };
    case "integrations-failed":
      return { ...state, integrationsError: event.message };
    case "deliveries-reading":
      return { ...state, deliveries: { ...state.deliveries, [event.connectionId]: null } };
    case "deliveries":
      return { ...state, deliveries: { ...state.deliveries, [event.connectionId]: event.deliveries } };
    case "deliveries-failed":
      return { ...state, deliveries: { ...state.deliveries, [event.connectionId]: event.message } };
    // `{ ...null }` is `{}`, so the three above start the map on the first read.
    case "delivery-retried": {
      // The retried row as the route answered it (#819), in its place; a list not read,
      // or read and refused, has no row to replace and is left as it is.
      const listed = state.deliveries?.[event.connectionId];
      if (!Array.isArray(listed)) return state;
      const replaced = listed.map((row) => (row.id === event.delivery.id ? event.delivery : row));
      return { ...state, deliveries: { ...state.deliveries, [event.connectionId]: replaced } };
    }
    case "setup":
      return { ...state, setup: event.setup };
    case "integrations-step":
      return { ...state, integrationsStep: event.step };
    case "reading":
      // A retry: the error goes before the read, so the screen does not show a
      // stale failure beside a spinner.
      return {
        ...state,
        // The list is re-read on a retry, so a failed read of it is not left standing.
        memberships: state.membershipsError === null ? state.memberships : null,
        membershipsError: null,
        runsError: null,
        rolesError: null,
        policyError: null,
        consentError: null,
        changesError: null,
        orgsError: null,
        customersError: null,
        membersError: null,
        invitesError: null,
        waitingError: null,
        inboxError: null,
        accessError: null,
        accountsError: null,
        sourcesError: null,
        providerError: null,
        integrationsError: null,
        spendError: null,
      };
  }
}
